import { useEffect, useState, useCallback, useRef } from "react";
import {
  MessageSquare,
  Mail,
  ImageIcon,
  Upload,
  Save,
  Trash2,
  Download,
  Power,
} from "lucide-react";
import { api } from "../../lib/api";
import logger from "../../lib/logger";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImage = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${BACKEND_URL}${path}`;
};

const DEFAULT_CONFIG = {
  active: "off",
  email_headline: "Stay in the loop",
  email_description: "Get notified about new DemoPod episodes and merch drops. No spam, ever.",
  promo: { image_url: "", headline: "", description: "", link_url: "" },
};

export default function AdminLightbox() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [exporting, setExporting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [cfg, subs] = await Promise.all([
        api.getLightboxConfig().catch(() => DEFAULT_CONFIG),
        api.getSubscribers().catch(() => []),
      ]);
      setConfig({ ...DEFAULT_CONFIG, ...cfg, promo: { ...DEFAULT_CONFIG.promo, ...(cfg?.promo || {}) } });
      setSubscribers(subs);
    } catch (err) {
      logger.warn("Lightbox admin fetch failed:", err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const setActive = (next) => {
    // Mutual exclusion: only one active at a time. Toggling the active one off returns to "off".
    setConfig((prev) => ({
      ...prev,
      active: prev.active === next ? "off" : next,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await api.updateLightboxConfig({
        active: config.active,
        email_headline: config.email_headline,
        email_description: config.email_description,
        promo: config.promo,
      });
      setConfig({ ...DEFAULT_CONFIG, ...saved, promo: { ...DEFAULT_CONFIG.promo, ...(saved.promo || {}) } });
      toast.success("Lightbox settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingImage(true);
    try {
      const result = await api.uploadAdminImage(file);
      setConfig((prev) => ({
        ...prev,
        promo: { ...prev.promo, image_url: result.url },
      }));
      toast.success("Image uploaded — remember to Save");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image upload failed");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleClearImage = () => {
    setConfig((prev) => ({ ...prev, promo: { ...prev.promo, image_url: "" } }));
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const response = await api.exportSubscribersCsv();
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.download = `subscribers-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteSubscriber = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the list?`)) return;
    try {
      await api.deleteSubscriber(id);
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
      toast.success("Subscriber removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="font-mono text-white/50 animate-pulse">Loading...</p>
      </div>
    );
  }

  const emailOn = config.active === "email";
  const promoOn = config.active === "promo";

  return (
    <div data-testid="admin-lightbox-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-russo text-3xl text-white uppercase tracking-tighter">
            LIGHTBOXES
          </h1>
          <p className="font-mono text-white/50 mt-2">
            Pop-up shown to first-time visitors on the home page. Only one can be active at a time.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-demolition-red text-white font-teko text-lg uppercase tracking-wider hover:bg-demolition-red-hover transition-colors disabled:opacity-50"
          data-testid="save-lightbox-config-btn"
        >
          <Save className="w-5 h-5" />
          {saving ? "Saving..." : "Save"}
        </button>
      </div>

      {/* Active state indicator */}
      <div
        className="mb-8 p-4 bg-demolition-paper border border-white/10 flex items-center gap-3"
        data-testid="active-lightbox-indicator"
      >
        <Power className={`w-5 h-5 ${config.active === "off" ? "text-white/30" : "text-demolition-red"}`} />
        <p className="font-mono text-white/80 text-sm">
          Active: <span className="font-russo text-white uppercase">{config.active}</span>
          {config.active !== "off" && " — visitors will see this lightbox once per browser session."}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
        {/* EMAIL CARD */}
        <div
          className={`bg-demolition-paper border p-6 ${emailOn ? "border-demolition-red" : "border-white/10"}`}
          data-testid="email-lightbox-card"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <Mail className="w-6 h-6 text-demolition-red" />
              <h2 className="font-teko text-xl text-white uppercase tracking-widest">
                Email Capture
              </h2>
            </div>
            <button
              onClick={() => setActive("email")}
              className={`relative inline-flex h-7 w-12 items-center transition-colors ${
                emailOn ? "bg-demolition-red" : "bg-white/10"
              }`}
              data-testid="toggle-email-lightbox"
              aria-label="Toggle email lightbox"
            >
              <span
                className={`inline-block h-5 w-5 bg-white transform transition-transform ${
                  emailOn ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="font-teko text-sm text-white/60 uppercase tracking-wider">
                Headline
              </Label>
              <Input
                value={config.email_headline}
                onChange={(e) => setConfig({ ...config, email_headline: e.target.value })}
                className="bg-demolition-subtle border-white/10 text-white font-mono mt-1"
                data-testid="email-headline-input"
              />
            </div>
            <div>
              <Label className="font-teko text-sm text-white/60 uppercase tracking-wider">
                Description
              </Label>
              <Textarea
                value={config.email_description}
                onChange={(e) => setConfig({ ...config, email_description: e.target.value })}
                rows={3}
                className="bg-demolition-subtle border-white/10 text-white font-mono mt-1 resize-none"
                data-testid="email-description-input"
              />
            </div>
          </div>
        </div>

        {/* PROMO CARD */}
        <div
          className={`bg-demolition-paper border p-6 ${promoOn ? "border-demolition-red" : "border-white/10"}`}
          data-testid="promo-lightbox-card"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-6 h-6 text-demolition-red" />
              <h2 className="font-teko text-xl text-white uppercase tracking-widest">
                Promo
              </h2>
            </div>
            <button
              onClick={() => setActive("promo")}
              className={`relative inline-flex h-7 w-12 items-center transition-colors ${
                promoOn ? "bg-demolition-red" : "bg-white/10"
              }`}
              data-testid="toggle-promo-lightbox"
              aria-label="Toggle promo lightbox"
            >
              <span
                className={`inline-block h-5 w-5 bg-white transform transition-transform ${
                  promoOn ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>

          <div className="space-y-4">
            {/* Image */}
            <div>
              <Label className="font-teko text-sm text-white/60 uppercase tracking-wider">
                Image
              </Label>
              {config.promo.image_url ? (
                <div className="mt-2 relative bg-demolition-subtle border border-white/10">
                  <img
                    src={resolveImage(config.promo.image_url)}
                    alt="Promo preview"
                    className="w-full max-h-48 object-cover"
                    data-testid="promo-image-preview"
                  />
                  <button
                    onClick={handleClearImage}
                    className="absolute top-2 right-2 p-2 bg-black/60 text-white hover:bg-black/80 transition-colors"
                    data-testid="clear-promo-image-btn"
                    aria-label="Remove image"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="mt-2 w-full p-6 border-2 border-dashed border-white/20 hover:border-demolition-red/60 transition-colors flex flex-col items-center gap-2 text-white/50 hover:text-white disabled:opacity-50"
                  data-testid="upload-promo-image-btn"
                >
                  {uploadingImage ? (
                    <p className="font-mono text-sm animate-pulse">Uploading...</p>
                  ) : (
                    <>
                      <Upload className="w-8 h-8" />
                      <span className="font-mono text-sm">Click to upload</span>
                      <span className="font-mono text-xs text-white/30">JPG, PNG, GIF, WEBP</span>
                    </>
                  )}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageFile}
                className="hidden"
                data-testid="promo-image-file-input"
              />
            </div>

            <div>
              <Label className="font-teko text-sm text-white/60 uppercase tracking-wider">
                Headline
              </Label>
              <Input
                value={config.promo.headline}
                onChange={(e) =>
                  setConfig({ ...config, promo: { ...config.promo, headline: e.target.value } })
                }
                placeholder="e.g. New Episode Out Now"
                className="bg-demolition-subtle border-white/10 text-white font-mono mt-1"
                data-testid="promo-headline-input"
              />
            </div>
            <div>
              <Label className="font-teko text-sm text-white/60 uppercase tracking-wider">
                Description
              </Label>
              <Textarea
                value={config.promo.description}
                onChange={(e) =>
                  setConfig({ ...config, promo: { ...config.promo, description: e.target.value } })
                }
                rows={3}
                placeholder="Short pitch for visitors..."
                className="bg-demolition-subtle border-white/10 text-white font-mono mt-1 resize-none"
                data-testid="promo-description-input"
              />
            </div>
            <div>
              <Label className="font-teko text-sm text-white/60 uppercase tracking-wider">
                Link URL (optional)
              </Label>
              <Input
                type="url"
                value={config.promo.link_url}
                onChange={(e) =>
                  setConfig({ ...config, promo: { ...config.promo, link_url: e.target.value } })
                }
                placeholder="https://..."
                className="bg-demolition-subtle border-white/10 text-white font-mono mt-1"
                data-testid="promo-link-input"
              />
              <p className="font-mono text-white/30 text-xs mt-1">
                If set, a "Learn more" button appears on the lightbox.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* SUBSCRIBERS */}
      <div className="bg-demolition-paper border border-white/10 p-6" data-testid="subscribers-section">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h2 className="font-teko text-xl text-white uppercase tracking-widest">
              Email Subscribers ({subscribers.length})
            </h2>
            <p className="font-mono text-white/40 text-xs mt-1">
              Visitors who opted in via the email lightbox.
            </p>
          </div>
          <button
            onClick={handleExportCsv}
            disabled={exporting || subscribers.length === 0}
            className="flex items-center gap-2 px-4 py-2 border border-demolition-red text-demolition-red font-teko text-base uppercase tracking-wider hover:bg-demolition-red hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
            data-testid="export-subscribers-csv-btn"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>

        {subscribers.length === 0 ? (
          <p className="font-mono text-white/40 text-sm py-6 text-center" data-testid="subscribers-empty">
            No subscribers yet.
          </p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="subscribers-list">
            {subscribers.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-3 py-2 px-3 bg-demolition-subtle border border-white/5"
                data-testid={`subscriber-${s.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-white text-sm truncate">{s.email}</p>
                  <p className="font-mono text-white/30 text-xs">
                    {s.created_at?.slice(0, 10)}{s.country ? ` · ${s.country}` : ""}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteSubscriber(s.id, s.email)}
                  className="p-2 text-white/40 hover:text-red-500 hover:bg-white/5 transition-colors flex-shrink-0"
                  data-testid={`delete-subscriber-${s.id}`}
                  aria-label={`Remove ${s.email}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
